import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef, useState } from 'react';
import { LIMITS } from '@tallyframe/core';
/**
 * Editor panel shown when the report header is selected (spec §6.1): title,
 * subtitle, "Prepared for" and the page setup the PDF is printed at. Mutually
 * exclusive with `WidgetInspector`, which the host swaps in when a widget is
 * selected instead.
 *
 * Each field commits on blur or on Enter, not on every keystroke — mirroring
 * apps/web's own title field (`ReportRoute.tsx`) — and emits exactly one
 * operation per commit: `rename_report` for the title, `set_report_details`
 * for subtitle and "Prepared for". An unchanged value emits nothing.
 *
 * "Unchanged" is trim-aware, matching both core's own handling of
 * `set_report_details` (which trims and treats all-whitespace as a clear)
 * and apps/web's `diffDefinitions`, which treats `undefined`, `''` and
 * whitespace-only as the same value. So a field that only gained trailing
 * spaces commits nothing; a field whose trimmed value did change commits the
 * trimmed value, never the raw one with its stray whitespace.
 */
function normalize(value) {
    return (value ?? '').trim();
}
function Field(props) {
    return (_jsxs("div", { className: "tf-field", children: [_jsx("label", { className: "tf-label", htmlFor: props.id, children: props.label }), props.children] }));
}
/**
 * A single text field with its own local draft, adopted from `incoming`
 * whenever that prop value genuinely changes (an undo, a refresh, a save
 * round-trip), and a `baseline` — the value the host is known to hold —
 * updated both when a new `incoming` is adopted and, optimistically, the
 * moment a commit is actually sent. The optimistic update matters: without
 * it, Enter followed by a Tab or a click elsewhere (both blur the field)
 * would compare the just-committed draft against a stale `incoming` (nothing
 * re-renders this component synchronously from a bare `vi.fn()` spy) and
 * fire the same operation twice.
 *
 * `isValid` gates what may ever become the baseline. Without it, `take()`
 * would advance the baseline for a value it is about to veto (an empty
 * title): the *next* commit — even retyping the exact title the host still
 * has — would then see a "changed" value and fire a redundant operation. So
 * a vetoed commit advances nothing and instead resets the draft back to the
 * last known-good baseline, the way apps/web's own title field snaps back to
 * `definition.title` rather than sitting on an invalid draft forever.
 */
function useDetailField(incoming, isValid = () => true) {
    const normalized = normalize(incoming);
    const [seen, setSeen] = useState(normalized);
    const [draft, setDraft] = useState(normalized);
    const baselineRef = useRef(normalized);
    let value = draft;
    if (normalized !== seen) {
        setSeen(normalized);
        setDraft(normalized);
        baselineRef.current = normalized;
        value = normalized;
    }
    return {
        value,
        setValue: setDraft,
        // Returns the trimmed value to commit, or null when nothing changed or
        // the value was rejected. Only a value that is both different and valid
        // ever advances the baseline.
        take() {
            const trimmed = draft.trim();
            if (trimmed === baselineRef.current)
                return null;
            if (!isValid(trimmed)) {
                setDraft(baselineRef.current);
                return null;
            }
            baselineRef.current = trimmed;
            return trimmed;
        }
    };
}
const nonEmpty = (trimmed) => trimmed.length > 0;
/**
 * Page setup: the paper the PDF is printed on.
 *
 * Unlike the text fields above, these have no draft and no commit step — a
 * `<select>` change *is* the finished gesture, so the value shown is always
 * `definition.print`, straight from props. That is what makes an undo, a save
 * round trip or a conflict reload visible here rather than leaving the panel
 * asserting a page setup the report no longer has.
 *
 * Orientation and page size are one decision for the operator, so they share a
 * row; `printChartSize` in `PrintReport.tsx` reads both to size printed charts.
 */
const ORIENTATIONS = [
    { value: 'portrait', label: 'Portrait' },
    { value: 'landscape', label: 'Landscape' }
];
const PAGE_SIZES = [
    { value: 'Letter', label: 'Letter' },
    { value: 'A4', label: 'A4' }
];
export function ReportDetailsInspector(props) {
    const { definition, onOperations } = props;
    const idBase = useId();
    // A definition with an empty title does not validate, so an empty commit
    // is rejected by `useDetailField` itself — it resets the draft rather than
    // handing back a value to send, and never advances the baseline for it.
    const title = useDetailField(definition.title, nonEmpty);
    const subtitle = useDetailField(definition.subtitle);
    const preparedFor = useDetailField(definition.preparedFor);
    const commitTitle = () => {
        const trimmed = title.take();
        if (trimmed === null)
            return;
        onOperations([{ op: 'rename_report', title: trimmed }]);
    };
    const commitSubtitle = () => {
        const trimmed = subtitle.take();
        if (trimmed === null)
            return;
        onOperations([{ op: 'set_report_details', subtitle: trimmed === '' ? null : trimmed }]);
    };
    const commitPreparedFor = () => {
        const trimmed = preparedFor.take();
        if (trimmed === null)
            return;
        onOperations([{ op: 'set_report_details', preparedFor: trimmed === '' ? null : trimmed }]);
    };
    const onEnter = (commit) => (event) => {
        if (event.key !== 'Enter')
            return;
        event.preventDefault();
        commit();
    };
    const onChange = (setValue) => (event) => {
        setValue(event.target.value);
    };
    /**
     * Sends the whole print block, not a one-key patch.
     *
     * Core would accept either — `set_print_options` merges a partial — but the
     * host's `diffDefinitions` emits `print: to.print` in full, and it is that
     * diff which rebuilds the pending batch after an undo. Matching it here
     * keeps one edit from reaching the server in two different shapes depending
     * on whether it arrived directly or via a resync.
     */
    const commitPrint = (change) => {
        onOperations([{ op: 'set_print_options', print: { ...definition.print, ...change } }]);
    };
    return (_jsxs("section", { className: "tf-panel", "aria-label": "Report details", children: [_jsx("h2", { className: "tf-panel-title", children: "Report details" }), _jsx(Field, { id: `${idBase}-title`, label: "Title", children: _jsx("input", { id: `${idBase}-title`, className: "tf-input", type: "text", maxLength: LIMITS.maxLabelChars, value: title.value, onChange: onChange(title.setValue), onBlur: commitTitle, onKeyDown: onEnter(commitTitle) }) }), _jsx(Field, { id: `${idBase}-subtitle`, label: "Subtitle", children: _jsx("input", { id: `${idBase}-subtitle`, className: "tf-input", type: "text", maxLength: LIMITS.maxSubtitleChars, value: subtitle.value, onChange: onChange(subtitle.setValue), onBlur: commitSubtitle, onKeyDown: onEnter(commitSubtitle) }) }), _jsx(Field, { id: `${idBase}-prepared-for`, label: "Prepared for", children: _jsx("input", { id: `${idBase}-prepared-for`, className: "tf-input", type: "text", maxLength: LIMITS.maxPreparedForChars, value: preparedFor.value, onChange: onChange(preparedFor.setValue), onBlur: commitPreparedFor, onKeyDown: onEnter(commitPreparedFor) }) }), _jsxs("div", { className: "tf-field-row", children: [_jsx(Field, { id: `${idBase}-orientation`, label: "Orientation", children: _jsx("select", { id: `${idBase}-orientation`, className: "tf-select", value: definition.print.orientation, onChange: (event) => commitPrint({ orientation: event.target.value }), children: ORIENTATIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) }) }), _jsx(Field, { id: `${idBase}-page-size`, label: "Page size", children: _jsx("select", { id: `${idBase}-page-size`, className: "tf-select", value: definition.print.pageSize, onChange: (event) => commitPrint({ pageSize: event.target.value }), children: PAGE_SIZES.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) }) })] })] }));
}
//# sourceMappingURL=ReportDetailsInspector.js.map